import {Navigate} from 'react-router'
import includes from 'lodash/includes'
import {Subjects} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async/index'
import {useCourses} from '@freckle/educator-entities/ts/common/hooks/use-courses'
import type {CourseSubjectT} from '@freckle/educator-entities/ts/roster/models/course-subject'
import {fetchCourseSubjects} from '@freckle/educator-entities/ts/roster/models/course-subject'
import {WithResources} from '@freckle/classroom/ts/common/components/with-resources'
import {logError} from '@freckle/classroom/ts/common/helpers/exception-handlers/bugsnag-client'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {useChromelessInaccessible} from '@freckle/classroom/ts/hooks/chromeless/use-chromeless-inaccessible'
import {useCurrentCourseId} from '@freckle/classroom/ts/hooks/use-current-course-id'

export default function AssignmentsHandler() {
  useChromelessInaccessible('Assignments')
  const courses = useCourses()
  const currentCourseId = useCurrentCourseId()

  const subjects = useAsync(() => fetchCourseSubjects(currentCourseId), 'fetchCourseSubjects', [
    currentCourseId,
  ])

  return (
    <WithResources
      resources={{courses, subjects}}
      render={({courses, subjects}) => {
        if (courses.length === 0) {
          const error = new Error(
            `No course could be found when trying to redirect to the assignments page`,
          )
          logError(error)
          // This happens when the user has no courses since the assignments
          // page requires a course to exist. This shouldn't really be possible
          // with the way we create a default course but, if it happens,
          // rostering is where we wanna send 'em.
          return <Navigate to={Routes.rostering.rosters()} replace />
        }
        return <DefaultTabRedirect courseSubjects={subjects} />
      }}
    />
  )
}

type Props = {
  courseSubjects: Array<CourseSubjectT>
}

function DefaultTabRedirect({courseSubjects}: Props) {
  const subjects = courseSubjects.map(({name}) => name)
  const {math, ela, socialStudies, science} = Subjects

  if (includes(subjects, math)) {
    return <Navigate to={Routes.math.assignments()} replace />
  } else if (includes(subjects, ela)) {
    return <Navigate to={Routes.ela.assignments()} replace />
  } else if (includes(subjects, socialStudies)) {
    return <Navigate to={Routes.socialStudies.assignments()} replace />
  } else if (includes(subjects, science)) {
    return <Navigate to={Routes.science.assignments()} replace />
  } else {
    return <Navigate to={Routes.math.assignments()} replace />
  }
}
